import { createJSONStorage, persist } from 'zustand/middleware'
import { createWithEqualityFn } from 'zustand/traditional'

import {
  ASYNC_EXPORT_JOBS_STORE_NAME,
  EXPORT_STALE_JOB_MS,
} from '~/features/exports/constants'
import {
  EExportJobType,
  EExportJobStatus,
  TAsyncExportJob,
  TExportJobMeta,
} from '~/features/exports/models'

type TAddJobInput = {
  jobId: string
  type: EExportJobType
  label: string
  queryParams: Record<string, string>
}

type TUpdateJobInput = {
  status?: EExportJobStatus
  error?: string
  downloadFilename?: string
  meta?: TExportJobMeta
  readyModalDismissed?: boolean
  readyModalShown?: boolean
}

type TAsyncExportJobsState = {
  jobs: TAsyncExportJob[]
  addJob: (job: TAddJobInput) => void
  updateJob: (jobId: string, patch: TUpdateJobInput) => void
  removeJob: (jobId: string) => void
  markModalDismissed: (jobId: string) => void
  markModalShown: (jobId: string) => void
  pruneStaleJobs: () => void
  clearJobs: () => void
  getJobsByType: (type: EExportJobType) => TAsyncExportJob[]
}

export const asyncExportJobsStore =
  createWithEqualityFn<TAsyncExportJobsState>()(
    persist(
      (set, get) => ({
        jobs: [],
        addJob: ({ jobId, type, label, queryParams }) => {
          set((state) => ({
            jobs: [
              ...state.jobs.filter((job) => job.jobId !== jobId),
              {
                jobId,
                type,
                label,
                queryParams,
                startedAt: Date.now(),
                status: EExportJobStatus.pending,
              },
            ],
          }))
        },
        updateJob: (jobId, patch) => {
          set((state) => ({
            jobs: state.jobs.map((job) =>
              job.jobId === jobId ? { ...job, ...patch } : job,
            ),
          }))
        },
        removeJob: (jobId) => {
          set((state) => ({
            jobs: state.jobs.filter((job) => job.jobId !== jobId),
          }))
        },
        markModalDismissed: (jobId) => {
          get().updateJob(jobId, { readyModalDismissed: true })
        },
        markModalShown: (jobId) => {
          get().updateJob(jobId, { readyModalShown: true })
        },
        pruneStaleJobs: () => {
          const cutoff = Date.now() - EXPORT_STALE_JOB_MS
          set((state) => ({
            jobs: state.jobs.filter((job) => job.startedAt >= cutoff),
          }))
        },
        clearJobs: () => {
          set({ jobs: [] })
        },
        getJobsByType: (type) => {
          return get().jobs.filter((job) => job.type === type)
        },
      }),
      {
        name: ASYNC_EXPORT_JOBS_STORE_NAME,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({ jobs: state.jobs }),
      },
    ),
  )

export default asyncExportJobsStore
